import { EMITTER_CONSTANTS, GRAPHQL_URL, SELECTED_PORTAL } from '@/shared/config';
import { generateToken, auth, emitter, showMessageError } from '@/shared/lib';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import type { DefaultOptions } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import type { ErrorResponse } from '@apollo/client/link/error';
import _ from 'lodash';
import { isMobileOnly } from 'react-device-detect';
import { t } from 'ttag';

type AuthRecoveryHandlers = {
  handleLoginAsClinic: (args: {
    isLoginThroughAdmin: any;
    userId: any;
    facilityId: any;
  }) => Promise<any>;
  fetchMe: () => Promise<any>;
  handleCheckRoles: (roles: any) => boolean;
};
let authRecoveryHandlers: AuthRecoveryHandlers | null = null;
/**
 * Wires the callbacks the Apollo error link uses to recover an expired or rejected session. Stores the supplied handler bundle in the module-level authRecoveryHandlers reference.
 *
 * @param handlers - Handlers invoked when a request fails authentication.
 * @returns Nothing.
 */
export const registerApolloAuthRecoveryHandlers = (handlers: AuthRecoveryHandlers) => {
  authRecoveryHandlers = handlers;
};

const cache = new InMemoryCache({ addTypename: false });

const defaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'ignore',
  },
  query: {
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  },
};

interface CreateClientOptions {
  selectedPortal?: string;
  isUsingCache?: boolean;
  isUsingAuthorizedToken?: boolean;
  isUsingToken?: boolean;
  uri?: string;
  isSkippingAuth?: boolean;
  refreshToken?: string;
}

/**
 * Builds an Apollo client for the portal's GraphQL API with auth headers and global error handling.
 * Chains an auth header link, an error link that retries lone internal or reset errors, recovers
 * invalid authorized tokens, re-checks roles and emits expired-token, maintenance or unauthorized
 * events, and an HTTP link, with caching off unless isUsingCache is set.
 *
 * @param options - Client options: selectedPortal, isUsingCache, isUsingAuthorizedToken,
 * isUsingToken, uri, isSkippingAuth and refreshToken.
 * @returns The configured Apollo client.
 */
const createClient = ({
  selectedPortal = SELECTED_PORTAL,
  isUsingCache = false,
  isUsingAuthorizedToken = true,
  isUsingToken = true,
  uri = GRAPHQL_URL,
  isSkippingAuth = false,
  refreshToken,
}: CreateClientOptions = {}) => {
  /**
   * Apollo context link that attaches authentication headers to every outgoing operation. Generates a fresh token when token auth is enabled (logging and ignoring generation failures), then merges `selected-portal` together with whichever access, refresh and authorized tokens are available into the request headers.
   *
   * @returns An Apollo context link that rewrites the request headers.
   */
  const authLink = setContext(async (request, { headers }) => {
    let token = null;
    if (isUsingToken) {
      try {
        token = await generateToken();
      } catch (error) {
        console.error('Failed to generate token: ', error);
        // emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN);
        // return false;
      }
    }
    const accessToken = token || auth.accessToken();
    const customHeaders = {
      ...headers,
      'selected-portal': selectedPortal,
    };
    if (accessToken) {
      _.assign(customHeaders, { 'access-token': accessToken });
    }
    if (refreshToken) {
      _.assign(customHeaders, { 'refresh-token': refreshToken });
    }
    if (isUsingAuthorizedToken && auth.authorizedToken()) {
      _.assign(customHeaders, { 'authorized-token': auth.authorizedToken() });
    }
    return { headers: customHeaders };
  });
  return new ApolloClient({
    link: authLink.concat(
      ApolloLink.from([
        onError(({ graphQLErrors, networkError, operation, forward }: ErrorResponse) => {
          if (graphQLErrors) {
            if (
              graphQLErrors.length === 1 &&
              (graphQLErrors[0].extensions?.code === 'INTERNAL_SERVER_ERROR' ||
                graphQLErrors[0].extensions?.code === 'ERESET')
            ) {
              return forward(operation);
            }

            _.forEach(graphQLErrors, ({ message, extensions }) => {
              if (message.includes('Authorized token is invalid')) {
                (async () => {
                  try {
                    const authorizedToken = await authRecoveryHandlers?.handleLoginAsClinic({
                      isLoginThroughAdmin: auth.isLoginThroughAdmin(),
                      userId: auth.userId(),
                      facilityId: auth.userFacilityId(),
                    });
                    auth.setAuthorizedToken(authorizedToken);
                    window.location.reload();
                  } catch (error) {
                    console.error('Failed to login as clinic: ', error);

                    emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN);
                  }
                })();
              } else if (message.includes('You are not authorized for this field')) {
                (async () => {
                  try {
                    const me = await authRecoveryHandlers?.fetchMe();
                    const isValid = authRecoveryHandlers?.handleCheckRoles(me?.roles);
                    if (!isValid) {
                      emitter.emit(EMITTER_CONSTANTS.ACCESS_REMOVED);
                    }
                  } catch (error) {
                    console.error('Failed to fetch me: ', error);

                    emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN);
                  }
                })();
              } else if (
                ((_.includes(message, '403') || _.includes(message, '400')) &&
                  !_.includes(message, 'Int cannot represent non 32-bit signed integer value')) ||
                extensions?.code === 'UNAUTHENTICATED'
              ) {
                emitter.emit(EMITTER_CONSTANTS.EXPIRED_TOKEN);
              }
            });
          } else if (networkError) {
            if ((networkError as any).statusCode === 503) {
              if (operation?.operationName === 'latestAppVersion') {
                return undefined;
              }

              emitter.emit(EMITTER_CONSTANTS.SITE_UNDER_MAINTENANCE);
              return undefined;
            }
            const loginFrom = auth.getLoginFrom();

            if (
              (networkError as any).statusCode === 403 &&
              loginFrom === 'Mercy' &&
              !isSkippingAuth
            ) {
              emitter.emit(EMITTER_CONSTANTS.SITE_UNAUTHORIZED);
              return undefined;
            }

            console.error(`[Network error]: ${networkError}`);
            showMessageError({ content: t`Please check your network connection and try again` });
          }

          return undefined;
        }),
        new HttpLink({
          uri,
          credentials: 'same-origin',
        }),
      ]),
    ),
    // name & version variables use for statistic purpose
    name: isMobileOnly ? 'clinic-portal-mobile' : 'clinic-portal-desktop',
    version: '1.232.3',
    cache,
    defaultOptions: isUsingCache ? undefined : defaultOptions,
  });
};

export default createClient;
